"use client";
import {
  Box,
  ButtonBase,
  Dialog,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {Iconify, TokenLogo} from "..";
import {Disclosure, useTokenList} from "../..";
import {useTranslations} from "next-intl";
import {TokenInfo} from "@uniswap/token-lists";
import InfiniteScroll from "react-infinite-scroll-component";
import CreditCardIcon from "@mui/icons-material/CreditCard";

export interface TokenExtraData {
  balance?: number;
  formattedBalance?: string;
  usdValue?: number;
  formattedUsdValue?: string;
};

interface AssetSelectDialogProps {
  disclosure: Disclosure;
  onSelect: (token: TokenInfo | "CARD") => void;
  hidden?: Record<string, boolean>;
  increment?: number;
  onDisplayChange?: (tokens: TokenInfo[]) => void;
  tokensExtraData?: Record<string, TokenExtraData>;
}

const StyledTextField = styled(OutlinedInput)(({ theme }) => ({
  borderRadius: "1rem",
  padding: theme.spacing(0.8, 1.6),
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:hover': {
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  '&.Mui-focused': {
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const ScrollableStack = styled(Stack)(({theme}) => ({
  borderRadius: "1rem",
  '& .infinite-scroll-component': {
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '0px',
      background: 'transparent', /* make scrollbar transparent */
    },
    scrollbarWidth: 'none', /* Firefox */
    '-ms-overflow-style': 'none',  /* Internet Explorer 10+ */
  }
}));

export const AssetSelectDialog = ({
  disclosure,
  onSelect,
  hidden = {},
  increment = 30,
  onDisplayChange,
  tokensExtraData = {},
}: AssetSelectDialogProps) => {
  const [length, setLength] = useState(increment);
  const t = useTranslations();
  const {query, setQuery, filteredTokens} = useTokenList();

  const filtered = useMemo(() => {
    return (
      filteredTokens?.filter((token) => {
        return !hidden[token.address];
      }) || []
    );
  }, [filteredTokens, hidden]);

  const displayTokens = useMemo(
    () => filtered.slice(0, length),
    [filtered, length]
  );

  useEffect(() => {
    setLength(increment);
  }, [filteredTokens, disclosure.isOpen]);

  const onNext = () => {
    setLength(length + increment);
  }

  useEffect(() => {
    if (!disclosure.isOpen) return;
    if (displayTokens.length === 0) return;
    onDisplayChange && onDisplayChange(displayTokens);
  }, [disclosure.isOpen, onDisplayChange, displayTokens]);

  return (
    <Dialog
      sx={{backgroundColor: "transparent"}}
      open={disclosure.isOpen}
      fullWidth={true}
      onClose={disclosure.onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.25)",
          },
        },
      }}
      PaperProps={{
        sx: {
          overflow: "hidden",
          maxWidth: "30rem",
          boxShadow: "0px 25px 50px 0px rgba(0, 0, 0, 0.12)",
          borderRadius: "1rem",
          display: 'flex',
          flexDirection: 'column',
          margin: {xs: "0.5rem", md: "1rem"},
          width: {xs: "calc(100% - 1rem)", md: "calc(100% - 32px)"},
          p: {xs: "1rem", md: "2rem"},
        },
      }}
    >
      <Stack spacing={2} sx={{flexGrow: 1, maxHeight: "calc(100vh - 4rem)", overflow: 'hidden'}}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight="500" lineHeight={1} fontSize="1.25rem">
            {t("common.select-assets")}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={disclosure.onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        {!hidden["CARD"] && (
          <CardSelectItem
            onSelect={() => {
              onSelect("CARD");
              disclosure.onClose();
            }}
          />
        )}
        <StyledTextField
          autoFocus
          size="small"
          placeholder={t("common.select-token")}
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          startAdornment={
            <InputAdornment position="start" sx={{pl: "0.2rem"}}>
              <Iconify icon="iconamoon:search-light" fontSize="1.4rem" />
            </InputAdornment>
          }
          fullWidth
        />
        <Typography mb={1} fontWeight="600" lineHeight="15.4px" fontSize="14px">
          {t("common.your-token")}
        </Typography>
        <ScrollableStack sx={{flexGrow: 1, overflow: 'hidden'}}>
          <Box sx={{flexGrow: 1, overflow: 'hidden', maxHeight: '300px'}}>
            <InfiniteScroll
              height="calc(100vh - 20rem)"
              next={onNext}
              hasMore={length < filtered.length}
              loader={<Typography>Loading...</Typography>}
              dataLength={Math.min(length, filtered.length)}
              endMessage={
                <Typography textAlign="center">
                  <b>No more items</b>
                </Typography>
              }
            >
              {displayTokens.map((token, index) => (
                <React.Fragment key={token.address}>
                  <AssetSelectItem
                    token={token}
                    tokenExtraData={tokensExtraData[token.address]}
                    onChange={(e) => {
                      onSelect(e);
                      disclosure.onClose();
                    }}
                  />
                  {index + 1 !== filtered.length && (
                    <Divider sx={{borderBottom: "1px solid #F7F7F7", mx: 1.5}} />
                  )}
                </React.Fragment>
              ))}
            </InfiniteScroll>
          </Box>
        </ScrollableStack>
      </Stack>
    </Dialog>
  );
};

interface ISelectItem  {
  token: TokenInfo;
  tokenExtraData?: TokenExtraData;

  onChange: (token: TokenInfo) => void;
}

const AssetSelectItem = ({
  token,
  tokenExtraData = {},
  onChange
}: ISelectItem) => {
  const {
    balance,
    formattedBalance,
    formattedUsdValue
  } = tokenExtraData;

  const showBalance = (balance ?? 0) > 0;
  return (
    <ButtonBase
      sx={{
        display: "flex",
        justifyContent: "space-between",
        padding: 1.5,
        width: "100%",
      }}
      onClick={() => onChange(token)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" gap="0.5rem">
          <TokenLogo token={token} />
          <Stack spacing={0.4} alignItems="flex-start" >
            <Typography fontWeight="500" lineHeight={1.1}>
              {token.symbol}
            </Typography>
            <Typography
              fontWeight="500"
              lineHeight={1.1}
              fontSize="0.875rem"
              sx={{opacity: "0.5"}}
            >
              {token.name}
            </Typography>
          </Stack>
        </Box>
        {showBalance && (
          <Stack spacing={0.4} alignItems="flex-end" >
            <Typography fontWeight="500" lineHeight={1.1}>
              ${formattedUsdValue}
            </Typography>
            <Typography
              fontWeight="500"
              lineHeight={1.1}
              fontSize="0.875rem"
              sx={{opacity: "0.5"}}
            >
              {formattedBalance}
            </Typography>
          </Stack>
        )}
      </Box>
    </ButtonBase>
  );
};

interface ICardSelectItem {
  onSelect: () => void;
}

const CardSelectItem = ({onSelect}: ICardSelectItem) => {
  return (
    <ButtonBase
      sx={{
        display: "flex",
        justifyContent: "space-between",
        padding: 1.5,
        border: "1px solid #EFEFEF",
        borderRadius: "16px",
      }}
      onClick={onSelect}
    >
      <Box display="flex" alignItems="center" gap="0.5rem">
        <CreditCardIcon />
        <Stack spacing={0.4} alignItems="flex-start">
          <Typography fontWeight="500" lineHeight={1.1}>
            Card
          </Typography>
          <Typography
            fontWeight="500"
            lineHeight={1.1}
            fontSize="0.875rem"
            sx={{opacity: "0.5"}}
          >
            USD
          </Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );
};
